import { FC, useMemo, useState } from 'react';
import { Table, Pagination } from '../../../../../components';
import { Button as SMButton, Theme, Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Delete, Edit } from '@mui/icons-material';
import { ISetsAndPalettesOptions, IResponse, IColorPalette } from '../../../../../models';
import { SetsAndPalettesFilters } from '../sets-and-palettes-filters';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { getSetType, OptionSetType } from '../sets-and-palettes';
import {
  deleteColorPaletteDetails,
  getColorPaletteList
} from '../../../../../fetch/color-palettes';
import { AddColorPaletteModal } from './add-color-palette';
import { useHistory } from 'react-router-dom';

interface IColorPaletteSets {
  selectedSetOption: ISetsAndPalettesOptions;
  setSelectedSetOption: React.Dispatch<React.SetStateAction<ISetsAndPalettesOptions>>;
  isModalOpen: boolean;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  OptionSetType: OptionSetType;
}

export const ColorPaletteSets: FC<IColorPaletteSets> = ({
  selectedSetOption,
  setSelectedSetOption,
  isModalOpen,
  setIsModalOpen,
  OptionSetType
}) => {
  const classes = useStyles();

  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [sortDirection, setSortDirection] = useState<{
    name?: 'Asc' | 'Desc';
    childCount?: 'Asc' | 'Desc';
  }>({
    name: 'Asc'
  });
  const [selectedSort, setSelectedSort] = useState<string>('name');
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [nameSearch, setNameSearch] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();

  const [filters, setFilters] = useState<{
    name?: string;
    childCount?: number;
  }>({});

  const handleEditOption = option => {
    history.push(
      `/development/options/option-sets-and-palettes/color-palette-sets/${option.colorPaletteId}`
    );
  };

  const handleDeleteOption = async (option: IColorPalette) => {
    await deleteColorPaletteDetails(option.colorPaletteId.toString());
  };

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  // Still using OptionSets, but will replace when data is available
  const {
    isLoading: isLoadingColorPaletteSets,
    data: colorPalettesSets,
    refetch: refetchingColorPaletteSets
  } = useQuery<IResponse<IColorPalette[]>, Error>(
    ['getColorPaletteSets', page, perPage, filters, selectedSort, sortDirection],
    () =>
      getColorPaletteList({
        page: page + 1,
        perPage,
        name: nameSearch,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort]
      }),
    {
      onSuccess: optionSets => {
        setRecordCount(optionSets.totalRecordCount);
      }
    }
  );
  const optionSetsData = useMemo(() => colorPalettesSets?.records ?? [], [colorPalettesSets]);

  const columns = [
    {
      Header: 'Name',
      accessor: 'name',
      isServerSorted: selectedSort === 'name',
      isServerSortedDesc: sortDirection.name === 'Desc',
      handleClickColumn: () => handleClickColumn('name')
    },
    {
      Header: 'Options',
      accessor: 'optionCount',
      isServerSorted: selectedSort === 'childCount',
      isServerSortedDesc: sortDirection.childCount === 'Desc',
      handleClickColumn: () => handleClickColumn('childCount')
    },
    {
      id: 'actions',
      accessor: '',
      Header: '',
      sort: false,
      Cell: ({
        cell: {
          row: { original }
        }
      }: {
        cell: { row: { original: any } };
      }) => {
        return (
          <div className={classes.actionButtons}>
            <SMButton
              id="edit-framer"
              type="button"
              color="primary"
              startIcon={<Edit />}
              onClick={() => handleEditOption(original)}
            >
              Edit
            </SMButton>
            <SMButton
              id="delete-framer"
              type="button"
              className={classes.deleteButton}
              startIcon={<Delete />}
              onClick={async () => {
                const result = window.confirm(
                  `Are you sure you want to delete this ${getSetType(OptionSetType)} Set?` +
                    (original?.optionCount > 0
                      ? ` This will also delete ${original?.optionCount} option${
                          original?.optionCount > 1 ? 's' : ''
                        }.`
                      : '')
                );
                if (result) {
                  try {
                    await handleDeleteOption(original);
                    enqueueSnackbar(`${getSetType(OptionSetType)} Deleted!`, {
                      variant: 'success'
                    });
                    refetchingColorPaletteSets();
                  } catch (error) {
                    console.log(error);
                    enqueueSnackbar(
                      error?.Detail ??
                        `Error deleting ${getSetType(OptionSetType)} Set, please try again`,
                      {
                        variant: 'error'
                      }
                    );
                  }
                }
              }}
            >
              Delete
            </SMButton>
          </div>
        );
      }
    }
  ];
  return (
    <Box mt={2}>
      <SetsAndPalettesFilters
        isLoading={isLoadingColorPaletteSets}
        handleNameSearch={(clearSearch?: boolean) => {
          setPage(0);
          setFilters({
            ...filters,
            name: nameSearch
          });

          if (clearSearch) {
            setNameSearch('');
          }
        }}
        applyFilters={(clearFilters?: boolean) => {
          if (clearFilters) {
            setFilters({
              name: ''
            });
          } else {
            setPage(0);
            setFilters({
              ...filters
            });
          }
        }}
        setPage={setPage}
        setHasAppliedFilters={setHasAppliedFilters}
        searchName={nameSearch}
        setSearchName={setNameSearch}
        hasAppliedFilters={hasAppliedFilters}
      />
      <Table
        columns={columns}
        data={optionSetsData}
        isLoading={isLoadingColorPaletteSets}
        hidePagination
        mobileProps={{
          handleEdit: (option: any) => handleEditOption(optionSetsData)
        }}
      />
      <Pagination
        page={page}
        count={recordCount}
        rowsPerPage={perPage}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
      />
      <AddColorPaletteModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedSetOption(null);
        }}
      />
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    marginTop: '1rem'
  },
  link: {
    textDecoration: 'none'
  },
  addOptionButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
      width: 'auto'
    }
  },
  actionButtons: {
    textAlign: 'right',
    display: 'flex'
  },
  tabBar: {
    marginTop: '-1rem',
    backgroundColor: '#fff',
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.grey[400]}`
  },
  buttonText: {
    color: 'white'
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  }
}));
